// Front.tsx
import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import applicationVersion from '../../../application_version.txt';
import MainPanel from '../components/MainPanel';
import store from '../store/store';
import { ROUTES } from '../utils/routes';
import BlogPostEditView from './Admin/BlogPosts/BlogPostEditView';
import BlogPostListView from './Admin/BlogPosts/BlogPostListView';
import BlogPostNewView from './Admin/BlogPosts/BlogPostNewView';
import CardHolderListView from './Admin/CardHolderListView';
import ClientsListView from './Admin/ClientsListView';
import ClientsPlanningView from './Admin/ClientsPlanningView';
import CoproCompanyValidationView from './Admin/CompanyCoproValidationView';
import CompanyDetailsView from './Admin/CompanyDetailsView';
import CompanyListView from './Admin/CompanyListView';
import CoproValidationView from './Admin/CoproValidationView';
import ImportDetailView from './Admin/ImportDetailView';
import ImportHistoryView from './Admin/ImportHistoryView';
import NewsletterEditView from './Admin/Newsletter/NewsletterEditView';
import AdminNewsletterListView from './Admin/Newsletter/NewsletterListView';
import AdminNewsletterPreviewView from './Admin/Newsletter/NewsletterPreviewView';
import NewsletterStructuredView from './Admin/Newsletter/NewsletterStructuredView';
import NewsletterTypeSelectionView from './Admin/Newsletter/NewsletterTypeSelectionView';
import PendingConfigImportsView from './Admin/PendingConfigImportsView';
import SciContactImportView from './Admin/SciContactImportView';
import SiretTrackedImportView from './Admin/SiretTrackedImportView';
import SoftwareImportView from './Admin/SoftwareImportView';
import ActivitySetupStepCoproValidationView from './Analyzing/ActivitySetupCoproValidationView';
import ActivitySetupAddressVerificationView from './Analyzing/ActivitySetupStepAddressVerificationView';
import ActivitySetupStepCoproAddView from './Analyzing/ActivitySetupStepCoproAddView';
import BoitageQualificationView from './Analyzing/BoitageQualificationView';
import BoitageTransactionView from './Analyzing/BoitageTransactionView';
import IncomingTransactionCoproReportView from './Analyzing/CommunicationCoproReportView';
import CoproDetailedListView from './Analyzing/CoproDetailedListView';
import CoproIDView from './Analyzing/CoproIDView';
import CoproKeyFiguresView from './Analyzing/CoproKeyFiguresView';
import CoproManagingView from './Analyzing/CoproManagingView';
import CoproVigilanceView from './Analyzing/CoproVigilanceView';
import GestionnairesCoproView from './Analyzing/GestionnairesCoproView';
import MarketVisionView from './Analyzing/MarketVision';
import SalesReportView from './Analyzing/SalesReportView';
import BuildingSaleBoitageView from './Communication/BuildingSaleBoitageView';
import GeneralAssemblyManagerView from './Communication/GeneralAssemblyManagerView';
import GeneralAssemblyReferentView from './Communication/GeneralAssemblyReferentView';
import NewsletterListView from './Communication/NewsletterListView';
import NewslettersPreviewView from './Communication/NewslettersPreviewView';
import NewsletterTemplateSelectionView from './Communication/NewsletterTemplateSelectionView';
import PageSiteView from './Communication/PageSiteView';
import ReferentAppointmentsView from './Communication/ReferentAppointmentsView';
import ReferentCampaignsView from './Communication/ReferentCampaignsView';
import ReferentFormationView from './Communication/ReferentFormationView';
import SciUnsubscribersView from './Communication/SciUnsubscribersView';
import UnsubscribersView from './Communication/UnsubscribersView';
import CroissanceView from './Croissance/CroissanceView';
import BauxAEcheanceView from './Monitoring/BauxAEcheanceView';
import BauxAllView from './Monitoring/BauxAllView';
import BauxCoproView from './Monitoring/BauxCoproView';
import BODACCView from './Monitoring/BODACCView';
import InsuranceExclusionsView from './Monitoring/InsuranceExclusionsView';
import SCIsOverallView from './Monitoring/SCIsOverallView';
import SCIsView from './Monitoring/SCIsView';
import SirenSCIsView from './Monitoring/SirenSCIsView';
import TrackedCompaniesView from './Monitoring/TrackedCompaniesView';
import NewDashboardView from './NewDashboard/NewDashboardView';
import ParametrageView from './NewDashboard/ParametrageView';
import BlockAccess from './Pages/BlockAccess';
// import ComingSoon from './Pages/ComingSoon';
import Logout from './Pages/Logout';
import Page404 from './Pages/Page404';
import SentryErrorFallback from './Pages/SentryErrorFallback';
import PaymentCheckoutView from './PaymentCheckout/PaymentCheckoutView';
import PaymentInvoiceView from './PaymentInvoice/PaymentInvoiceView';
import DPEReportView from './Prospecting/DPEReportView';
import DpeVisionView from './Prospecting/DpeVisionView';
// import IdentifiedDPEView from './Prospecting/IdentifiedDPEView';
import NewDPEView from './Prospecting/NewDPEView';
import OwnerCommunicationView from './Prospecting/OwnerCommunicationView';
import PerformanceView from './Prospecting/PerformanceView';
import RentalMeetingView from './Prospecting/RentalMeetingView';
import SalesMeetingView from './Prospecting/SalesMeetingView';
import SCIsReferentView from './Prospecting/SCIsReferentView';
import SCIsRentView from './Prospecting/SCIsRentView';
import SCIsTransactionView from './Prospecting/SCIsTransactionView';
import QRCodeAssistantView from './QRCodeAssistantView';
import ActivitySetupCoproAreaView from './Setup/ActivitySetupCoproAreaView';
import ActivitySetupStepNotificationView from './Setup/ActivitySetupNotificationView';
import ActivitySetupStepCollaboratorView from './Setup/ActivitySetupStepCollaboratorView';
import ActivitySetupTransactionQualificationView from './Setup/ActivitySetupTransactionQualificationView';
import NafExclusionView from './Setup/NafExclusionView';
import SetupBrandingView from './Setup/SetupBrandingView';
import SetupMailConfigView from './Setup/SetupMailConfigView';
import SetupSentMailsView from './Setup/SetupSentMailsView';
import SimplifiedSetupDashboardView from './Setup/SimplifiedSetupDashboardView';


Sentry.init({
  dsn: 'https://daeb15b9737b88747a5317125698f767@demeter.capturama.immo/2',
  environment: applicationVersion,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    'localhost',
    /^(http:\/\/(dev|preprod)\.rgpdcopro\.immo|https:\/\/rgpd-copro\.fr)/,
    'https://demeter.capturama.immo',
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  enabled: applicationVersion !== 'v0.0.0',
});

const router = createBrowserRouter([
  {
    path: ROUTES.HOME,
    element: <Sentry.ErrorBoundary fallback={SentryErrorFallback}>
      <MainPanel />
    </Sentry.ErrorBoundary>,
    errorElement: <SentryErrorFallback />,
    children: [
      {
        path: ROUTES.HOME,
        element: <NewDashboardView />,
      },
      {
        path: ROUTES.BLOCKED_ACCESS,
        element: <BlockAccess />,
      },
      {
        path: ROUTES.LOGOUT,
        element: <Logout />,
      },
      {
        path: ROUTES.DASHBOARD,
        element: <Navigate to={ROUTES.HOME} replace />,
      },
      {
        path: ROUTES.NEW_DASHBOARD_PARAMETRAGE,
        element: <ParametrageView />,
      },
      {
        path: ROUTES.QR_CODE_ASSISTANT,
        element: <QRCodeAssistantView />,
      },
      // -- CROISSANCE --
      {
        path: ROUTES.CROISSANCE,
        element: <CroissanceView />,
      },
      // -- PAYMENT --
      {
        path: ROUTES.PAYMENT_CHECKOUT,
        element: <PaymentCheckoutView />,
      },
      {
        path: ROUTES.PAYMENT_INVOICE,
        element: <PaymentInvoiceView />,
      },
      // -- SETUP ACTIVITE --
      {
        path: ROUTES.SETUP_SIMPLIFIED_DASHBOARD,
        element: <SimplifiedSetupDashboardView />,
      },
      {
        path: ROUTES.ANALYZING_VERIFY_COPROPRIETES,
        element: <ActivitySetupStepCoproValidationView />,
      },
      {
        path: ROUTES.ANALYZING_ADD_ADDRESSES,
        element: <ActivitySetupAddressVerificationView />,
      },
      {
        path: ROUTES.ANALYZING_ADD_COPROPRIETES,
        element: <ActivitySetupStepCoproAddView />,
      },
      {
        path: ROUTES.SETUP_TRANSACTION_QUALIFICATION,
        element: <ActivitySetupTransactionQualificationView />,
      },
      {
        path: ROUTES.SETUP_COPRO_AREA,
        element: <ActivitySetupCoproAreaView />,
      },
      {
        path: ROUTES.SETUP_ADD_COLLABORATORS,
        element: <ActivitySetupStepCollaboratorView />,
      },
      {
        path: ROUTES.SETUP_CONFIGURE_NOTIFICATIONS,
        element: <ActivitySetupStepNotificationView />,
      },
      {
        path: ROUTES.SETUP_BRANDING,
        element: <SetupBrandingView />,
      },
      {
        path: ROUTES.SETUP_MAIL_CONFIG,
        element: <SetupMailConfigView />,
      },
      {
        path: ROUTES.SETUP_SENT_MAILS,
        element: <SetupSentMailsView />,
      },
      {
        path: ROUTES.SETUP_NAF_EXCLUSION,
        element: <NafExclusionView />,
      },
      // -- Analyzing --
      {
        path: ROUTES.ANALYZING_GENERATE_SALES_REPORT,
        element: <SalesReportView />,
      },
      {
        path: ROUTES.ANALYZING_MARKET_VISION,
        element: <MarketVisionView />,
      },
      {
        path: ROUTES.ANALYZING_COPRO_MANAGING,
        element: <CoproManagingView />,
      },
      {
        path: ROUTES.ANALYZING_COPRO_MANAGING_ID,
        element: <CoproIDView />,
      },
      {
        path: ROUTES.ANALYZING_COPRO_VIGILANCE,
        element: <CoproVigilanceView />,
      },
      {
        path: ROUTES.COMMUNICATION_GENERAL_ASSEMBLY_REFERENT,
        element: <GeneralAssemblyReferentView />,
      },
      {
        path: ROUTES.COMMUNICATION_GENERAL_ASSEMBLY_MANAGER,
        element: <GeneralAssemblyManagerView />,
      },
      {
        path: ROUTES.ANALYZING_GESTIONNAIRES_COPROS,
        element: <GestionnairesCoproView />,
      },
      {
        path: ROUTES.ANALYZING_GENERATE_COPRO_REPORT,
        element: <IncomingTransactionCoproReportView />,
      },
      
      // -- Prospecting --
      {
        path: ROUTES.PROSPECTING_DPE_VISION,
        element: <DpeVisionView />,
      },
      {
        path: ROUTES.PROSPECTING_NEW_OPPORTUNITIES,
        element: <NewDPEView />,
      },
      {
        // Currently not used
        path: ROUTES.PROSPECTING_COPRO_DETAILED_LIST,
        element: <CoproDetailedListView />,
      },
      {
        path: ROUTES.PROSPECTING_COPRO_KEY_FIGURES,
        element: <CoproKeyFiguresView />,
      },
      {
        path: ROUTES.PROSPECTING_BOITAGE_QUALIFICATION,
        element: <BoitageQualificationView />,
      },
      {
        path: ROUTES.PROSPECTING_BOITAGE_TRANSACTION,
        element: <BoitageTransactionView />,
      },
      // {
        //   path: ROUTES.PROSPECTING_IDENTIFIED_OWNER,
        //   element: <IdentifiedDPEView />,
        // },
      // TODO: refacto to remove this step

      {
        path: ROUTES.PROSPECTING_COMMUNICATE,
        element: <OwnerCommunicationView />,
      },
      {
        path: ROUTES.PROSPECTING_TRANSACTION,
        element: <SalesMeetingView />,
      },
      {
        path: ROUTES.PROSPECTING_SCIS_TRANSACTION,
        element: <SCIsTransactionView />,
      },
      {
        path: ROUTES.PROSPECTING_SCIS_RENT,
        element: <SCIsRentView />,
      },
      {
        path: ROUTES.PROSPECTING_SCIS_REFERENT,
        element: <SCIsReferentView />,
      },
      {
        path: ROUTES.PROSPECTING_RENTAL,
        element: <RentalMeetingView />,
      },
      {
        path: ROUTES.PROSPECTING_TEAM_PERFORMANCE,
        element: <PerformanceView />,
      },
      {
        path: ROUTES.PROSPECTING_GENERATE_DPE_REPORT,
        element: <DPEReportView />,
      },
      
      // -- MONITORING --
      {
        path: ROUTES.MONITORING_SCIS,
        element: <SCIsView />,
      },
      {
        path: ROUTES.MONITORING_SCIS_OVERALL,
        element: <SCIsOverallView />,
      },
      {
        path: ROUTES.MONITORING_SCIS_SIREN,
        element: <SirenSCIsView />,
      },
      {
        path: ROUTES.MONITORING_BAUX_COMMERCIAUX,
        element: <BauxAllView />,
      },
      {
        path: ROUTES.MONITORING_BAUX_A_ECHEANCE,
        element: <BauxAEcheanceView />,
      },
      {
        path: ROUTES.MONITORING_BAUX_COMMERCIAUX_COPRO,
        element: <BauxCoproView />,
      },
      {
        path: ROUTES.MONITORING_ENTREPRISES_EN_DEFAILLANCE,
        element: <BODACCView />,
      },
      {
        path: ROUTES.MONITORING_ENTREPRISES_SUIVIES,
        element: <TrackedCompaniesView />,
      },
      {
        path: ROUTES.MONITORING_INSURANCE_EXCLUSIONS,
        element: <InsuranceExclusionsView />,
      },
      // -- QUICK ACCESS --
      
      // -- COMMUNICATION --
      {
        path: ROUTES.COMMUNICATION_UNSUBSCRIBERS,
        element: <UnsubscribersView />,
      },
      {
        path: ROUTES.COMMUNICATION_SCI_UNSUBSCRIBERS,
        element: <SciUnsubscribersView />,
      },

      {
        path: ROUTES.COMMUNICATION_NEWSLETTER_LIST,
        element: <NewsletterListView />,
      },
      {
        path: ROUTES.COMMUNICATION_NEWSLETTER_TEMPLATE_SELECTION,
        element: <NewsletterTemplateSelectionView />,
      },
      {
        path: ROUTES.COMMUNICATION_NEWSLETTER_PREVIEW,
        element: <NewslettersPreviewView />,
      },
      {
        path: ROUTES.COMMUNICATION_PAGE_SITE,
        element: <PageSiteView />,
      },
      {
        path: ROUTES.COMMUNICATION_REFERENT_CAMPAIGNS,
        element: <ReferentCampaignsView />,
      },
      {
        path: ROUTES.COMMUNICATION_REFERENT_FORMATION,
        element: <ReferentFormationView />,
      },
      {
        path: ROUTES.COMMUNICATION_BUILDING_SALE_BOITAGE,
        element: <BuildingSaleBoitageView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS,
        element: <ReferentAppointmentsView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS_NEW,
        element: <ReferentAppointmentsView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS_SCHEDULED,
        element: <ReferentAppointmentsView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS_TRACKING,
        element: <ReferentAppointmentsView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS_TEMPLATE_CLIENT,
        element: <ReferentAppointmentsView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS_TEMPLATE_COLLAB,
        element: <ReferentAppointmentsView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS_TEMPLATE_RELANCE,
        element: <ReferentAppointmentsView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS_TEAM_SALES,
        element: <ReferentAppointmentsView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS_TEAM_RENTAL,
        element: <ReferentAppointmentsView />,
      },
      {
        path: ROUTES.REFERENT_APPOINTMENTS_TEAM_PLANNING,
        element: <ReferentAppointmentsView />,
      },
      // -- ADMIN --
      { // redirect to ADMIN_COMPANIES_LIST
        path: ROUTES.ADMIN_DASHBOARD,
        element: <Navigate to={ROUTES.ADMIN_COMPANIES_LIST} replace />,
      },
      {
        path: ROUTES.ADMIN_SOFTWARE_IMPORT,
        element: <SoftwareImportView />,
      },
      {
        path: ROUTES.ADMIN_SCI_CONTACT_IMPORT,
        element: <SciContactImportView />,
      },
      {
        path: ROUTES.ADMIN_IMPORT_HISTORY,
        element: <ImportHistoryView />,
      },
      {
        path: ROUTES.ADMIN_IMPORT_DETAIL,
        element: <ImportDetailView />,
      },
      {
        path: ROUTES.ADMIN_COMPANY_VERIFICATION,
        element: <CoproCompanyValidationView />,
      },
      {
        path: ROUTES.ADMIN_COPRO_VALIDATION,
        element: <CoproValidationView />,
      },
      {
        path: ROUTES.ADMIN_COMPANIES_LIST,
        element: <CompanyListView />,
      },
      {
        path: ROUTES.ADMIN_CLIENTS_LIST,
        element: <ClientsListView />,
      },
      {
        path: ROUTES.ADMIN_CLIENTS_PLANNING,
        element: <ClientsPlanningView />,
      },
      {
        path: ROUTES.ADMIN_CARD_HOLDERS_LIST,
        element: <CardHolderListView />,
      },
      {
        path: ROUTES.ADMIN_COMPANY_DETAILS,
        element: <CompanyDetailsView />,
      },
      {
        path: ROUTES.ADMIN_SIRET_TRACKED_IMPORT,
        element: <SiretTrackedImportView />,
      },
      {
        path: ROUTES.ADMIN_BLOG_POSTS,
        element: <BlogPostListView />,
      },
      {
        path: ROUTES.ADMIN_BLOG_POST_NEW,
        element: <BlogPostNewView />,
      },
      {
        path: ROUTES.ADMIN_BLOG_POST_EDIT,
        element: <BlogPostEditView />,
      },
      {
        path: ROUTES.ADMIN_NEWSLETTER,
        element: <AdminNewsletterListView />,
      },
      {
        path: ROUTES.ADMIN_NEWSLETTER_EDIT,
        element: <NewsletterEditView />,
      },
      {
        path: ROUTES.ADMIN_NEWSLETTER_SELECT_TYPE,
        element: <NewsletterTypeSelectionView />,
      },
      {
        path: ROUTES.ADMIN_NEWSLETTER_NEW_STRUCTURED,
        element: <NewsletterStructuredView />,
      },
      {
        path: ROUTES.ADMIN_NEWSLETTER_PREVIEW,
        element: <AdminNewsletterPreviewView />,
      },
      {
        path: ROUTES.ADMIN_PENDING_CONFIG_IMPORTS,
        element: <PendingConfigImportsView />,
      },
      {
        path: '*',
        element: <Page404 />,
      },
    ],
  },
]);

function Front() {
  return (
    <Provider store={store}>
      <Sentry.ErrorBoundary fallback={SentryErrorFallback}>
        <RouterProvider router={router} />
      </Sentry.ErrorBoundary>
    </Provider>
  );
}

export default Front;
